import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import { Select } from "../Select";
import { Toggle } from "../Toggle";
import useApiHelper from "../../../hooks/useApiHelper";
import useQSBuilder from "../../../hooks/useQSBuilder";
import { IBaseModel } from "../../../models/BaseModel";
import {
  PredefinedTableFilterField,
  TableFilter,
  TableFilterField,
} from "./TableFilter";
import { IDefaultPaginatedResponse } from "../../../models/DefaultResponse";
import {
  TableFilterContextProvider,
  useTableFilter,
} from "./TableFilterContext";
import LoadingWheel from "../../shared/LoadingWheel";
import { useDebounce } from "@uidotdev/usehooks";
import TableBulkActions, { BulkAction } from "./TableBulkActions";

function ensureArray<T>(input: T | T[] | null | undefined): T[] {
  if (input === null || input === undefined) {
    return [];
  }

  if (Array.isArray(input)) {
    return input;
  }

  return [input];
}

export interface TableSortState {
  sortBy: string;
  sortDescending: boolean;
}

export interface TableProps<T extends IBaseModel> {
  url: string;
  queryKey: string[];
  columns: ColumnDef<T, any>[];
  filterFields?: TableFilterField[];
  externalFilters?: any;
  predefinedFilterFields?: PredefinedTableFilterField[];
  searchLabel?: string;
  enableShowDeleted?: boolean;
  bulkActions?: BulkAction[];
  /** Groups the bulk actions behind one toggle button with this label, as Core's tables do. */
  bulkActionsLabel?: string;
  /**
   * Opt in to server-side sorting. Columns declare their key via
   * `meta: { sortKey: "created" }`; columns without one stay unsortable.
   */
  sortable?: boolean;
  defaultSort?: TableSortState;
  /** Makes rows clickable. Cell-level controls should stop propagation. */
  onRowClick?: (row: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Rendered above the search bar, inside the table's own layout. */
  toolbar?: React.ReactNode;
  /** Rendered in the same row as the search bar, alongside it. */
  inlineFilters?: React.ReactNode;
  /** Hides the built-in total-count query for endpoints that don't support it. */
  hideTotalCount?: boolean;
}

export function Table<T extends IBaseModel>({
  url,
  queryKey,
  columns,
  filterFields,
  externalFilters,
  predefinedFilterFields,
  searchLabel,
  bulkActions = [],
  bulkActionsLabel,
  enableShowDeleted = false,
  sortable = false,
  defaultSort,
  onRowClick,
  toolbar,
  inlineFilters,
  hideTotalCount = false,
}: TableProps<T>) {
  return (
    <TableFilterContextProvider
      predefinedFilterFields={predefinedFilterFields}
      filterFields={filterFields}
    >
      <TableImpl
        url={url}
        queryKey={queryKey}
        columns={columns}
        filterFields={filterFields}
        externalFilters={externalFilters}
        enableShowDeleted={enableShowDeleted}
        predefinedFilterFields={predefinedFilterFields}
        searchLabel={searchLabel}
        bulkActions={bulkActions}
        bulkActionsLabel={bulkActionsLabel}
        sortable={sortable}
        defaultSort={defaultSort}
        onRowClick={onRowClick}
        toolbar={toolbar}
        inlineFilters={inlineFilters}
        hideTotalCount={hideTotalCount}
      />
    </TableFilterContextProvider>
  );
}

function TableImpl<T extends IBaseModel>({
  url,
  queryKey,
  columns,
  filterFields,
  externalFilters,
  predefinedFilterFields,
  searchLabel,
  enableShowDeleted,
  bulkActions,
  bulkActionsLabel,
  sortable,
  defaultSort,
  onRowClick,
  toolbar,
  inlineFilters,
  hideTotalCount,
}: TableProps<T>) {
  const { get } = useApiHelper();
  const buildQS = useQSBuilder();
  const { filters, getFilterQueryString } = useTableFilter();
  const defaultData = useMemo(() => [], []);
  const pageSizeOptions = [5, 10, 20, 50, 100, 200, 500];
  const [rawSearch, setRawSearch] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [sort, setSort] = useState<TableSortState | undefined>(defaultSort);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const searchTerm = useDebounce(rawSearch, 300);
  const external = useDebounce(externalFilters, 300);

  const fetchDataOptions = useMemo(() => ({
    pageNumber: pageIndex,
    itemsPerPage: pageSize,
    includeDeleted,
    searchTerm,
    ...(sortable && sort
      ? { sortBy: sort.sortBy, sortDescending: sort.sortDescending }
      : {}),
    ...external
  }), [pageIndex, pageSize, includeDeleted, searchTerm, sortable, sort, external]);

  /** Cycles a column asc -> desc -> back to the default sort. */
  const toggleSort = (sortKey: string) => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSort((prev) => {
      if (prev?.sortBy !== sortKey) return { sortBy: sortKey, sortDescending: false };
      if (!prev.sortDescending) return { sortBy: sortKey, sortDescending: true };
      return defaultSort;
    });
  };

  const dataQuery = useQuery({
    queryKey: [...queryKey, { ...fetchDataOptions, ...filters }],
    queryFn: () =>
      get<IDefaultPaginatedResponse<T>>(
        `${url}${buildQS(fetchDataOptions)}${getFilterQueryString()}`
      ),
    placeholderData: keepPreviousData,
  });

  const totalCount = useQuery({
    queryKey: [...queryKey, "totalCount"],
    queryFn: () =>
      get<IDefaultPaginatedResponse<T>>(
        `${url}?pageNumber=0&itemsPerPage=1`
      ),
    placeholderData: keepPreviousData,
    enabled: !hideTotalCount,
    staleTime(query) {
      // Prevents the total count from being refetched on every page change
      return query.state.dataUpdatedAt === 0 ? 0 : 1000 * 60 * 5; // 5 minutes
    },
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const table = useReactTable({
    data: ensureArray(dataQuery.data?.data) ?? defaultData,
    columns,
    pageCount: dataQuery.data?.count
      ? Math.ceil(dataQuery.data.count / pageSize)
      : 1,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    // Selection is keyed by id rather than row index, so a refetch that shifts the
    // rows (a new ticket arriving at the top) can't move it onto different records.
    getRowId: (row, index) => (row.id != null ? String(row.id) : String(index)),
  });

  useEffect(() => {
    table.setRowSelection({});
  }, [fetchDataOptions, pagination.pageIndex, pagination.pageSize])

  // Compared by content, not identity: callers may pass a fresh externalFilters
  // object on every render, which would otherwise reset the page constantly.
  const filterSignature = JSON.stringify({
    searchTerm,
    external,
    includeDeleted,
    filters,
  });

  // Narrowing the result set can leave you on a page that no longer exists,
  // so any change to the filters drops back to the first page.
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [filterSignature]);

  if (dataQuery.isLoading) {
    return (
      <div className="flex py-10 justify-center items-center">
        <LoadingWheel />
      </div>
    );
  } else {
    return (
      <div>
        {toolbar}
        {filterFields || predefinedFilterFields ? (
          <TableFilter filterFields={filterFields} />
        ) : (
          <></>
        )}
        {enableShowDeleted && (
          <Toggle
            label="Show Deleted"
            checked={includeDeleted}
            onChange={(value) => setIncludeDeleted(value)}
            className="my-4"
          />
        )}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          {inlineFilters}
          {
            searchLabel ? <div className="relative flex items-stretch focus-within:z-10">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="text"
                onChange={(e) => setRawSearch(e.target.value)}
                className="pl-10 py-2 border sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)]"
                placeholder={searchLabel!}
                style={{ width: `calc(${searchLabel!.length}ch - 2rem)` }}
              />
            </div> : null
          }
        </div>
        {bulkActions && <TableBulkActions table={table} bulkActions={bulkActions} groupLabel={bulkActionsLabel} /> }
        <div className="flex justify-between">
          <small className="text-gray-500 mb-2">
            {dataQuery.data?.count ?? 0} items found
          </small>

          {/* total unfiltered count */}
          {!hideTotalCount && totalCount.data?.count && (
            <small className="text-gray-500 mb-2">
              {totalCount.data.count} total items
            </small>
          )}
        </div>
        <div className="block overflow-x-auto overflow-y-hidden">
          <table className="min-w-full table border border-orange-600 divide-y divide-orange-600">
            <thead className="bg-orange-600/40">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const sortKey = (header.column.columnDef.meta as any)?.sortKey as
                      | string
                      | undefined;
                    const isSortable = sortable && !!sortKey;
                    const isSorted = isSortable && sort?.sortBy === sortKey;

                    return (
                      <th
                        key={header.id}
                        className="px-3 py-3 text-left text-sm font-semibold"
                      >
                        {header.isPlaceholder ? null : isSortable ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(sortKey!)}
                            className="inline-flex items-center gap-1 hover:text-orange-900"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {isSorted ? (
                              sort?.sortDescending ? (
                                <ChevronDownIcon className="h-4 w-4" />
                              ) : (
                                <ChevronUpIcon className="h-4 w-4" />
                              )
                            ) : (
                              <ChevronUpDownIcon className="h-4 w-4 opacity-40" />
                            )}
                          </button>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-orange-600/40">
              {table.getRowModel().rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  onClick={
                    onRowClick ? (e) => onRowClick(row.original, e) : undefined
                  }
                  className={clsx(
                    `${rowIndex % 2 !== 0 ? "bg-orange-600/20" : "bg-white"}`,
                    row.original.deleted
                      ? "bg-red-400/50 opacity-70 italic"
                      : "",
                    onRowClick ? "cursor-pointer hover:bg-orange-600/30" : "",
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="whitespace-nowrap px-3 py-4 text-sm"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="w-full ml-auto mt-2 flex flex-col items-center space-y-2 lg:space-y-0 lg:flex-row lg:justify-between">
          <span className="flex items-center gap-1">
            <div>Page</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </span>
          <div>
            <button
              type="button"
              className={`rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 
            ${!table.getCanPreviousPage() &&
                "hover:!cursor-default !text-gray-400 hover:!bg-paper"
                }
            `}
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronDoubleLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200
            ${!table.getCanPreviousPage() &&
                "hover:!cursor-default !text-gray-400 hover:!bg-paper"
                }
            `}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 
            ${!table.getCanNextPage() &&
                "hover:!cursor-default !text-gray-400 hover:!bg-paper"
                }
            `}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200
            ${!table.getCanNextPage() &&
                "hover:!cursor-default !text-gray-400 hover:!bg-paper "
                }
            `}
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <ChevronDoubleRightIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <span>Per Page</span>
            <Select
              small
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.currentTarget.value));
              }}
            >
              {pageSizeOptions.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </div>
    );
  }
}
